import * as T from "three";
import { interpolateTransform } from "./live-timeline.js";
import { Component } from "./live-codec.js";

/** Resolves streamed relationships after all independent world transforms and poses. */
export class LiveParenting {
    constructor() { this.names = new WeakMap(); this.orders = new WeakMap(); this.matches = new WeakMap(); this.inverse = new T.Matrix4(); this.local = new T.Matrix4(); this.offset = new T.Object3D(); this.parentMatrix = new T.Matrix4(); this.inverseScale = new T.Vector3(); }
    boneNames(rig) {
        let names = this.names.get(rig);
        if (!names) { names = new Map(rig.bones.map(bone => [bone.name.toLowerCase(), bone])); this.names.set(rig, names); }
        return names;
    }
    boneOrder(rig) {
        let order = this.orders.get(rig);
        if (!order) {
            order = []; const visited = new Set();
            const append = index => {
                if (visited.has(index)) return;
                visited.add(index);
                const parent = rig.data.bones[index].parent;
                if (parent >= 0) append(parent);
                order.push(index);
            };
            rig.bones.forEach((_, index) => append(index)); this.orders.set(rig, order);
        }
        return order;
    }
    boneMatches(rig, parentRig) {
        let parents = this.matches.get(rig);
        if (!parents) { parents = new WeakMap(); this.matches.set(rig, parents); }
        let matches = parents.get(parentRig);
        if (!matches) {
            const names = this.boneNames(parentRig);
            matches = this.boneOrder(rig).map(index => {
                const bone = rig.bones[index];
                return { bone, source: names.get(bone.name.toLowerCase()) };
            });
            parents.set(parentRig, matches);
        }
        return matches;
    }
    merge(entity, parent) {
        if (!entity.rig || !parent.rig) return false;
        let merged = false, inverseParent = null;
        for (const { bone, source } of this.boneMatches(entity.rig, parent.rig)) {
            if (source) {
                // Convert the parent's posed world bone into the child's local
                // hierarchy; the child's supplied world motion is not added twice.
                if (inverseParent !== bone.parent) {
                    this.inverse.copy(bone.parent.matrixWorld).invert(); inverseParent = bone.parent;
                }
                this.local.multiplyMatrices(this.inverse, source.matrixWorld);
                this.local.decompose(bone.position, bone.quaternion, bone.scale);
                merged = true;
            }
            bone.updateWorldMatrix(false, false);
        }
        // Bones have already been updated in hierarchy order. Only attachment
        // descendants need another pass before downstream attachments resolve.
        // Studio attachments have matrixAutoUpdate=false. Their local matrix
        // stays fixed, but their world matrix must follow the newly merged bone.
        for (const attachment of entity.rig.attachments) attachment.updateMatrixWorld(true);
        entity.boneMergedLast = merged;
        return merged;
    }
    attach(entity, parent, relationship, time) {
        if (!(relationship.flags & 4)) return;
        const target = relationship.attachment ? parent.rig?.attachments[relationship.attachment - 1] : parent.object;
        if (!target) return; // Keep the validated world transform if the attachment is unavailable.
        const pose = Number.isFinite(time) ? interpolateTransform(entity.state.parentHistory, time) : null;
        this.offset.position.fromArray(pose?.position ?? relationship.localPosition);
        const [pitch, yaw, roll] = pose?.angles ?? relationship.localAngles;
        this.offset.rotation.set(roll * Math.PI / 180, pitch * Math.PI / 180, yaw * Math.PI / 180, "ZYX");
        this.offset.scale.copy(entity.object.scale); this.offset.updateMatrix();
        this.inverse.copy(entity.object.parent.matrixWorld).invert();
        this.parentMatrix.copy(target.matrixWorld);
        // EntityToWorldTransform excludes studio model scale. Origin-parented
        // worldtext must stay 40 units above a 2.85x viewer eye, not 114 units.
        // Bone attachments retain their posed/scaled transform.
        if (!relationship.attachment) this.parentMatrix.scale(this.inverseScale.set(
            1 / parent.object.scale.x, 1 / parent.object.scale.y, 1 / parent.object.scale.z));
        this.local.multiplyMatrices(this.inverse, this.parentMatrix).multiply(this.offset.matrix);
        this.local.decompose(entity.object.position, entity.object.quaternion, entity.object.scale);
        entity.object.updateMatrixWorld(true);
    }
    update(entities, root, time) {
        root.updateMatrixWorld(true);
        const visited = new Map();
        const resolve = (entity, depth = 0) => {
            if (visited.has(entity)) return visited.get(entity) === 2;
            if (depth >= 32) return false;
            visited.set(entity, 1);
            const relationship = entity.state.components[Component.Parent];
            if (relationship?.index && relationship.flags & 5) {
                const parent = entities.get(`${relationship.index}:${relationship.generation}`);
                if (parent && parent !== entity && resolve(parent, depth + 1)) {
                    if (relationship.flags & 1) this.merge(entity, parent);
                    else this.attach(entity, parent, relationship, time);
                }
                else if (parent) { visited.set(entity, 3); return false; }
            }
            visited.set(entity, 2); return true;
        };
        for (const entity of entities.values()) resolve(entity);
    }
}
